/* iter55g15 — Consolidated open/closed state for the editor drawers.
 *
 * The EditorPanel used to hold six independent useState pairs
 * (history/plagiarism/spell/bible/search/bookmarks/suggestions plus a
 * bookmark-selection seed). Extracting them into one hook keeps the
 * panel body focused on composition and gives the drawers a single
 * contract for openers and toggles.
 *
 * The setters are STABLE-referenced so callers can pass them to memo'd
 * children without triggering re-renders.
 */
import { useCallback, useState } from 'react';

const INITIAL = {
  history:     false,
  plagiarism:  false,
  spell:       false,
  bible:       false,
  search:      false,
  bookmarks:   false,
  suggestions: false,
};

export function useEditorDrawers() {
  const [state, setState] = useState(INITIAL);
  const [pendingBookmarkSel, setPendingBookmarkSel] = useState('');

  const setOpen = useCallback((key, value) => {
    setState((prev) => ({ ...prev, [key]: typeof value === 'function' ? value(prev[key]) : Boolean(value) }));
  }, []);
  const toggle = useCallback((key) => {
    setState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const open = useCallback((key) => setOpen(key, true), [setOpen]);
  const close = useCallback((key) => setOpen(key, false), [setOpen]);

  // Bookmark drawer wants to receive a seed selection when opened from
  // the manuscript body — expose that here so the panel doesn't have to
  // thread two setters.
  const openBookmarks = useCallback((seedSelection = '') => {
    setPendingBookmarkSel(seedSelection);
    setOpen('bookmarks', true);
  }, [setOpen]);

  return {
    // booleans
    historyOpen:     state.history,
    plagiarismOpen:  state.plagiarism,
    spellOpen:       state.spell,
    bibleOpen:       state.bible,
    searchOpen:      state.search,
    bookmarksOpen:   state.bookmarks,
    suggestionsOpen: state.suggestions,
    // controls
    setOpen, toggle, open, close, openBookmarks,
    pendingBookmarkSel, setPendingBookmarkSel,
  };
}
